import React, { useState } from 'react'
import type {
  SelectableListItem,
  SelectableListProps,
} from './SelectableList.types'
import {
  SELECTABLE_LIST_DEMO_ITEMS,
  SELECTABLE_LIST_DEMO_TITLE,
} from './SelectableList.types'
import './SelectableList.css'

const SelectableList: React.FC<SelectableListProps> = ({
  title,
  titleMeta,
  items,
  value,
  defaultValue,
  onChange,
  maxHeight = 342,
  width = 250,
  className = '',
  style,
  darkMode = false,
  previewState,
}) => {
  const isPreview = previewState != null
  const isControlled = value !== undefined

  const [uncontrolledValue, setUncontrolledValue] = useState<
    string | undefined
  >(defaultValue ?? SELECTABLE_LIST_DEMO_ITEMS[0]?.id)

  const resolvedItems = ((): SelectableListItem[] => {
    if (items != null) {
      return items
    }

    if (!isPreview) {
      return []
    }

    if (previewState === 'disabled-item') {
      return SELECTABLE_LIST_DEMO_ITEMS.map((item, index) =>
        index === 1 ? { ...item, disabled: true } : item,
      )
    }

    return SELECTABLE_LIST_DEMO_ITEMS
  })()

  const displayTitle = (() => {
    if (title !== undefined) {
      return title
    }
    if (previewState === 'no-header') {
      return undefined
    }
    if (isPreview) {
      return SELECTABLE_LIST_DEMO_TITLE
    }
    return undefined
  })()

  const displayTitleMeta = (() => {
    if (previewState === 'no-header' || previewState === 'no-meta') {
      return undefined
    }
    if (titleMeta !== undefined) {
      return titleMeta
    }
    if (isPreview) {
      const count = resolvedItems.length
      return `${count} ${count === 1 ? 'Claim' : 'Claims'}`
    }
    return undefined
  })()

  const showHeader = displayTitle != null || displayTitleMeta != null

  const selectedId = (() => {
    if (isPreview) {
      switch (previewState) {
        case 'hover':
          return resolvedItems[0]?.id
        case 'selected':
          return resolvedItems[1]?.id ?? resolvedItems[0]?.id
        case 'no-header':
          return resolvedItems[2]?.id ?? resolvedItems[0]?.id
        default:
          return resolvedItems[0]?.id
      }
    }

    if (isControlled) {
      return value
    }

    return uncontrolledValue
  })()

  const handleSelect = (id: string): void => {
    if (isPreview) {
      return
    }

    if (!isControlled) {
      setUncontrolledValue(id)
    }

    onChange?.(id)
  }

  const listMaxHeight =
    typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight
  const panelWidth = typeof width === 'number' ? `${width}px` : width

  const classNames = [
    'ds-selectable-list',
    darkMode ? 'ds-selectable-list--dark' : '',
    isPreview ? 'ds-selectable-list--preview' : '',
    previewState ? `ds-selectable-list--preview-${previewState}` : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const ariaLabel =
    typeof displayTitle === 'string' ? displayTitle : 'Selectable list'

  return (
    <div
      className={classNames}
      style={{ width: panelWidth, ...style }}
      aria-label={ariaLabel}
    >
      {showHeader ? (
        <header className="ds-selectable-list__header">
          {displayTitle != null ? (
            <h3 className="ds-selectable-list__title">{displayTitle}</h3>
          ) : (
            <span />
          )}
          {displayTitleMeta != null ? (
            <span className="ds-selectable-list__title-meta">
              {displayTitleMeta}
            </span>
          ) : null}
        </header>
      ) : null}

      <ul
        className="ds-selectable-list__list"
        role="listbox"
        aria-label={ariaLabel}
        style={{ maxHeight: listMaxHeight }}
      >
        {resolvedItems.map((item, index) => {
          const selected = item.id === selectedId
          const previewHover = previewState === 'hover' && index === 1

          const buttonClass = [
            'ds-selectable-list__button',
            selected ? 'ds-selectable-list__button--selected' : '',
            item.disabled ? 'ds-selectable-list__button--disabled' : '',
            previewHover ? 'ds-selectable-list__button--preview-hover' : '',
          ]
            .filter(Boolean)
            .join(' ')

          return (
            <li
              key={item.id}
              className="ds-selectable-list__item"
              role="option"
              aria-selected={selected}
              aria-disabled={item.disabled || undefined}
            >
              <button
                type="button"
                className={buttonClass}
                disabled={item.disabled}
                tabIndex={isPreview ? -1 : undefined}
                onClick={() => handleSelect(item.id)}
              >
                <span className="ds-selectable-list__side">
                  <span className="ds-selectable-list__primary">
                    {item.primary}
                  </span>
                  {item.secondary != null ? (
                    <span className="ds-selectable-list__secondary">
                      {item.secondary}
                    </span>
                  ) : null}
                </span>
                {item.meta != null || item.status != null ? (
                  <span className="ds-selectable-list__side ds-selectable-list__side--end">
                    {item.meta != null ? (
                      <span className="ds-selectable-list__meta">
                        {item.meta}
                      </span>
                    ) : null}
                    {item.status != null ? (
                      <span className="ds-selectable-list__status">
                        {item.status}
                      </span>
                    ) : null}
                  </span>
                ) : null}
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

export default SelectableList
